import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CreateInquiryStep2_1 } from '@webapp/core/interfaces';
import { OBJECT_TYPE_OPTIONS, ROOM_OPTIONS } from '@webapp/core/constants/inquiry-labels';
import { ToastrService } from 'ngx-toastr';
import { GooglePlacesService } from 'projects/webapp/app/services/google-places.service';
import {
  InquiryDraftSource,
  PublicInquiryManagerService,
} from 'projects/webapp/app/services/public-inquiry-manager.service';


/** Objekt: the home a cleaning-only inquiry is about. */
@Component({
  selector: 'app-inquiry-step2-1',
  templateUrl: './step2.1.component.html',
})
export class InquiryStep21Component implements OnInit, OnDestroy {
  draftInquiryForm = new FormGroup({
    from_address: new FormControl('', { validators: Validators.required, nonNullable: true }),
    from_zip: new FormControl('', { validators: Validators.required, nonNullable: true }),
    from_city: new FormControl('', { validators: Validators.required, nonNullable: true }),
    from_object_type: new FormControl('', { validators: Validators.required, nonNullable: true }),
    rooms: new FormControl('', { validators: Validators.required, nonNullable: true }),
    square_meters: new FormControl(
      '',
      { validators: [Validators.required, Validators.min(10), Validators.max(500)], nonNullable: true }
    ),
  });

  roomOptions = ROOM_OPTIONS;

  objectTypeOptions = OBJECT_TYPE_OPTIONS;

  protected _draftSource: InquiryDraftSource = () => ({ step2_1: this._buildPayload() });

  constructor(
    protected _toastrService: ToastrService,
    protected _publicInquiryManagerService: PublicInquiryManagerService,
    private _googlePlacesService: GooglePlacesService,
  ) {
  }

  ngOnInit() {
    this._loadFormData();
    this._publicInquiryManagerService.setDraftSource(this._draftSource);
  }

  ngOnDestroy() {
    this._publicInquiryManagerService.clearDraftSource(this._draftSource);
  }

  onSubmit() {
    this.draftInquiryForm.markAllAsTouched();
    if (!this.draftInquiryForm.valid) {
      this._toastrService.warning('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    this._publicInquiryManagerService.submitStep2_1(this._buildPayload())
  }

  hasError(controlName: string, errorName?: string) {
    const control = this.draftInquiryForm.get(controlName);
    const isValid = errorName ? !control?.hasError(errorName) : !control?.invalid;
    return !!control?.touched && !isValid;
  }

  isObjectTypeActive(value: string) {
    return this.draftInquiryForm.controls.from_object_type.value === value;
  }

  selectObjectType(value: string) {
    this.draftInquiryForm.controls.from_object_type.setValue(value);
  }

  goBack() {
    this._publicInquiryManagerService.goPrevStep()
  }

  async onZipPlaceSelected(place: google.maps.places.Place) {
    if (!place.addressComponents) return;

    const get = (type: string) =>
      place.addressComponents?.find((c: google.maps.places.AddressComponent) => c.types.includes(type))?.longText ?? '';

    const postalCode = get('postal_code');
    const locality = get('locality');

    this.draftInquiryForm.patchValue({ from_zip: postalCode, from_city: locality });

    const currentAddress = this.draftInquiryForm.controls.from_address.value;
    if (currentAddress && locality) {
      const found = await this._googlePlacesService.hasAddressInCity(currentAddress, locality);
      if (!found) this.draftInquiryForm.controls.from_address.setValue('');
    }
  }

  async onCityPlaceSelected(place: google.maps.places.Place) {
    if (!place.addressComponents) return;

    const get = (type: string) =>
      place.addressComponents?.find((c: google.maps.places.AddressComponent) => c.types.includes(type))?.longText ?? '';

    const locality = get('locality');

    let postalCode = get('postal_code');

    if (locality && !postalCode) {
      const currentPostalCode = this.draftInquiryForm.controls.from_zip.value;
      const found = await this._googlePlacesService.hasCityPostalCode(currentPostalCode, locality);
      postalCode = found ? currentPostalCode : '';
    }
    this.draftInquiryForm.patchValue({ from_city: locality, from_zip: postalCode });

    const currentAddress = this.draftInquiryForm.controls.from_address.value;
    if (currentAddress && locality) {
      const found = await this._googlePlacesService.hasAddressInCity(currentAddress, locality);
      if (!found) this.draftInquiryForm.controls.from_address.setValue('');
    }
  }

  onAddressPlaceSelected(place: google.maps.places.Place) {
    if (!place.addressComponents) return;

    const get = (type: string) =>
      place.addressComponents?.find((c: google.maps.places.AddressComponent) => c.types.includes(type))?.longText ?? '';

    const streetNumber = get('street_number');
    const route = get('route');
    const postalCode = get('postal_code');
    const locality = get('locality');

    const streetParts = [route, streetNumber].filter(el => !!el);
    const address = streetParts.join(' ') || place.formattedAddress?.split(',')[0]?.trim() || '';

    this.draftInquiryForm.patchValue({
      from_address: address,
      ...(postalCode ? { from_zip: postalCode } : {}),
      ...(locality ? { from_city: locality } : {}),
    });
  }

  protected _loadFormData() {
    const step2_1Data = this._publicInquiryManagerService.inquiryCreationProgress?.step2_1;
    if (step2_1Data) this.draftInquiryForm.patchValue(step2_1Data);
  }

  protected _buildPayload(): CreateInquiryStep2_1 {
    return this.draftInquiryForm.getRawValue();
  }
}
